import { useState, useEffect, useCallback } from 'react';
import menuConfigData from '../config/menuConfig.json';
import { userAPI } from '../services/api.js';
import PageUsageService from '../utils/pageUsageService.ts';

interface MenuItem {
  visible: boolean;
  label: string;
  path?: string;
  icon: string;
  uncollapsible?: boolean;
  children?: Record<string, MenuItem>;
}

interface MenuConfig {
  menuConfig: Record<string, MenuItem>;
  userPreferences: {
    defaultCollapsedSections: string[];
    hiddenItems: string[];
    customOrder: string[];
    theme: string;
    compactMode: boolean;
    showIcons: boolean;
    groupSimilarItems: boolean;
  };
  metadata: {
    version: string;
    lastUpdated: string;
    description: string;
  };
}

// Matches AGENT_DOMAINS (client/src/config/agentDomains.ts) — the same ids
// Settings' Domain Folders and onboarding's category picker both read/write
// via user.onboarding.domains. Domains with a real DOMAIN_WORKFLOWS config
// route to /domains/:id; the rest route to the Agents page pre-filtered to
// that domain instead of a placeholder demo page.
const DOMAIN_FOLDER_MAP: Record<string, { label: string; icon: string; path: string }> = {
  general: { label: 'General', icon: 'Sparkles', path: '/agents?domain=general' },
  // Coding capability is supplied by an explicitly configured MCP integration,
  // rather than a first-party Engineering or repository-intelligence workspace.
  coding: { label: 'Coding', icon: 'FileCode', path: '/mcp-servers' },
  finance: { label: 'Finance', icon: 'Receipt', path: '/domains/finance' },
  product: { label: 'Product', icon: 'Boxes', path: '/agents?domain=product' },
  marketing: { label: 'Marketing', icon: 'Megaphone', path: '/agents?domain=marketing' },
  hr: { label: 'HR', icon: 'Users', path: '/domains/hr' },
  legal: { label: 'Legal', icon: 'Shield', path: '/domains/legal' },
  founders: { label: 'Founders', icon: 'Rocket', path: '/agents?domain=founders' },
};

const DOMAIN_EVENT = 'lyra:domains-changed';
const DEFAULT_COLLAPSED_SECTIONS = ['Developer Tools'];

export const useMenuConfig = () => {
  const [config, setConfig] = useState<MenuConfig>(menuConfigData as MenuConfig);
  const [collapsedSections, setCollapsedSections] = useState<string[]>([]);
  const [quickAccessItems, setQuickAccessItems] = useState<Record<string, MenuItem>>({});
  const [isLoadingQuickAccess, setIsLoadingQuickAccess] = useState(false);
  const [userDomains, setUserDomains] = useState<string[]>([]);

  // Map categories to icons (fallback)
  const getIconForCategory = useCallback((category: string): string => {
    const categoryIconMap: Record<string, string> = {
      'finance': 'DollarSign',
      'tools': 'Wrench',
      'dev-utils': 'Code',
      'project-management': 'CheckSquare',
      'personal': 'User',
      'dashboard': 'Home',
      'settings': 'Settings',
      'chat': 'MessageSquare',
      'calculator': 'Calculator',
      'timer': 'Timer',
      'default': 'Zap'
    };
    
    return categoryIconMap[category] || categoryIconMap.default;
  }, []);

  // Find icon from menu configuration based on path or title
  const findIconFromMenuConfig = useCallback((pagePath: string, pageTitle: string): string => {
    // Search through the entire menu configuration to find matching path or title
    const searchInMenu = (menuObj: Record<string, MenuItem>): string | null => {
      for (const [, item] of Object.entries(menuObj)) {
        // Direct path match
        if (item.path === pagePath) {
          return item.icon;
        }
        
        // Title match (case insensitive)
        if (item.label.toLowerCase() === pageTitle.toLowerCase()) {
          return item.icon;
        }
        
        // Partial title match for variations like "Calculator Hub" vs "Salary Calculator"
        const itemWords = item.label.toLowerCase().split(' ');
        const pageWords = pageTitle.toLowerCase().split(' ');
        
        for (const itemWord of itemWords) {
          for (const pageWord of pageWords) {
            if (itemWord.length > 3 && pageWord.length > 3 && 
                (itemWord.includes(pageWord) || pageWord.includes(itemWord))) {
              return item.icon;
            }
          }
        }
        
        // Search in children recursively
        if (item.children) {
          const childIcon = searchInMenu(item.children);
          if (childIcon) return childIcon;
        }
      }
      return null;
    };

    const foundIcon = searchInMenu(config.menuConfig);
    return foundIcon || getIconForCategory('default');
  }, [config.menuConfig, getIconForCategory]);

  // Load user preferences from localStorage on mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('menuPreferences');
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences);
        setConfig(prev => ({
          ...prev,
          userPreferences: { ...prev.userPreferences, ...preferences }
        }));
        const savedCollapsed = Array.isArray(preferences.defaultCollapsedSections)
          ? preferences.defaultCollapsedSections
          : [];
        setCollapsedSections(savedCollapsed.length > 0 ? savedCollapsed : DEFAULT_COLLAPSED_SECTIONS);
      } catch (error) {
        console.error('Error loading menu preferences:', error);
        setCollapsedSections(DEFAULT_COLLAPSED_SECTIONS);
      }
    } else {
      setCollapsedSections(DEFAULT_COLLAPSED_SECTIONS);
    }
  }, []);

  // Load Quick Access items from API
  useEffect(() => {
    const loadQuickAccessItems = async () => {
      setIsLoadingQuickAccess(true);
      try {
        // API automatically excludes Dashboard and AI Assistant pages
        const quickAccessPages = await PageUsageService.getQuickAccessRecommendations(4);
        
        if (quickAccessPages.length > 0) {
          const dynamicQuickAccess: Record<string, MenuItem> = {};
          
          quickAccessPages.forEach((page, index) => {
            // Map page data to menu item format
            const key = `quickAccess_${index}`;
            // First try to find icon from menu config, then fallback to category mapping
            const icon = findIconFromMenuConfig(page.pagePath, page.pageTitle);
            
            dynamicQuickAccess[key] = {
              visible: true,
              label: page.pageTitle,
              path: page.pagePath,
              icon: icon
            };
          });
          
          setQuickAccessItems(dynamicQuickAccess);
        }
      } catch (error) {
        console.error('Error loading quick access items:', error);
        // Fallback to static items if API fails
        setQuickAccessItems({});
      } finally {
        setIsLoadingQuickAccess(false);
      }
    };

    loadQuickAccessItems();
  }, [findIconFromMenuConfig]);

  // Load user preferences (domains) so we can conditionally show/hide sections
  useEffect(() => {
    const loadUserPrefs = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setUserDomains([]);
          return;
        }
        const res = await userAPI.getUserPreferences();
        const domains = res?.preferences?.domains || [];
        setUserDomains(Array.isArray(domains) ? domains.map((d: string) => d.toLowerCase()) : []);
      } catch (err) {
        console.warn('Failed to load user preferences for menu filtering', err);
        setUserDomains([]);
      }
    };

    loadUserPrefs();

    const onDomainsChanged = (event: any) => {
      const next = event?.detail?.domains;
      if (Array.isArray(next)) {
        setUserDomains(next.map((d: string) => String(d).toLowerCase()));
      }
    };

    window.addEventListener(DOMAIN_EVENT, onDomainsChanged);
    return () => window.removeEventListener(DOMAIN_EVENT, onDomainsChanged);
  }, []);

  // Save preferences to localStorage
  const savePreferences = (preferences: Partial<MenuConfig['userPreferences']>) => {
    const updatedPreferences = { ...config.userPreferences, ...preferences };
    setConfig(prev => ({
      ...prev,
      userPreferences: updatedPreferences
    }));
    localStorage.setItem('menuPreferences', JSON.stringify(updatedPreferences));
  };

  // Toggle item visibility
  const toggleItemVisibility = (itemPath: string) => {
    const hiddenItems = config.userPreferences.hiddenItems;
    const isHidden = hiddenItems.includes(itemPath);
    
    const updatedHiddenItems = isHidden
      ? hiddenItems.filter(item => item !== itemPath)
      : [...hiddenItems, itemPath];
    
    savePreferences({ hiddenItems: updatedHiddenItems });
  };

  // Toggle section collapse
  const toggleSectionCollapse = (sectionKey: string) => {
    const isCollapsed = collapsedSections.includes(sectionKey);
    const updatedCollapsed = isCollapsed
      ? collapsedSections.filter(key => key !== sectionKey)
      : [...collapsedSections, sectionKey];
    
    setCollapsedSections(updatedCollapsed);
    savePreferences({ defaultCollapsedSections: updatedCollapsed });
  };

  // Get filtered menu items based on visibility and user preferences
  const getVisibleMenuItems = () => {
    const { menuConfig, userPreferences } = config;
    const filteredMenu: Record<string, MenuItem> = {};

    const domainChildren: Record<string, MenuItem> = {};
    const enabledDomains = Array.isArray(userDomains) ? userDomains : [];

    if (enabledDomains.length > 0) {
      enabledDomains.forEach((domainId) => {
        const spec = DOMAIN_FOLDER_MAP[domainId];
        if (!spec) return;
        domainChildren[domainId] = {
          visible: true,
          label: spec.label,
          path: spec.path,
          icon: spec.icon,
        };
      });
    } else {
      domainChildren.empty = {
        visible: true,
        label: 'Choose Domains',
        path: '/settings/profile#domains',
        icon: 'Settings',
      };
    }

    const domainsSection: MenuItem = {
      visible: true,
      label: 'Domains',
      icon: 'Grid',
      uncollapsible: true,
      children: domainChildren,
    };

    Object.entries(menuConfig).forEach(([key, item]) => {
      // Skip if item is hidden by user
      if (userPreferences.hiddenItems.includes(key)) {
        return;
      }

      // Include item if it's visible in config
      if (item.visible) {
        const filteredItem: MenuItem = { ...item };
        
        // Special handling for Quick Access section
        if (key === 'quickAccess') {
          // Use dynamic quick access items if available, otherwise use static config
          if (Object.keys(quickAccessItems).length > 0) {
            filteredItem.children = quickAccessItems;
          } else {
            // Use original children from config as fallback
            filteredItem.children = item.children || {};
          }
          filteredMenu[key] = filteredItem;
          return;
        }
        
        // Filter children if they exist
        if (item.children) {
          const filteredChildren: Record<string, MenuItem> = {};
          
          Object.entries(item.children).forEach(([childKey, childItem]) => {
            const childPath = `${key}.${childKey}`;

            // Domain-based gating for nested menu entries.
            // These items are domain-owned and should only appear when enabled.
            if (childKey === 'developerTools' && !enabledDomains.includes('coding')) {
              return;
            }
            if (childKey === 'projectManagement' && !enabledDomains.includes('coding')) {
              return;
            }
            if (childKey === 'repos' && !enabledDomains.includes('coding')) {
              return;
            }
            if (childKey === 'engineering' && !enabledDomains.includes('coding')) {
              return;
            }
            
            // Skip if child is hidden by user
            if (userPreferences.hiddenItems.includes(childPath)) {
              return;
            }
            
            if (childItem.visible) {
              const filteredChild: MenuItem = { ...childItem };
              
              // Filter grandchildren if they exist
              if (childItem.children) {
                const filteredGrandChildren: Record<string, MenuItem> = {};
                
                Object.entries(childItem.children).forEach(([grandChildKey, grandChildItem]) => {
                  const grandChildPath = `${key}.${childKey}.${grandChildKey}`;
                  
                  // Skip if grandchild is hidden by user
                  if (userPreferences.hiddenItems.includes(grandChildPath)) {
                    return;
                  }
                  
                  if (grandChildItem.visible) {
                    filteredGrandChildren[grandChildKey] = grandChildItem;
                  }
                });
                
                // Only include child if it has visible grandchildren or is a direct item
                if (Object.keys(filteredGrandChildren).length > 0 || childItem.path) {
                  filteredChild.children = filteredGrandChildren;
                  filteredChildren[childKey] = filteredChild;
                }
              } else {
                filteredChildren[childKey] = filteredChild;
              }
            }
          });
          
          // Only include parent if it has visible children or is a direct item
          if (Object.keys(filteredChildren).length > 0 || item.path) {
            filteredItem.children = filteredChildren;
            filteredMenu[key] = filteredItem;
          }
        } else {
          filteredMenu[key] = filteredItem;
        }
      }
    });

    const coreChildren = filteredMenu.CORE?.children || {};
    const buildChildren = filteredMenu.BUILD?.children || {};
    const workspaceChildren = filteredMenu.WORKSPACE?.children || {};
    const platformChildren = filteredMenu.PLATFORM?.children || {};

    const homeChildren: Record<string, MenuItem> = {};
    if (coreChildren.dashboard) homeChildren.dashboard = coreChildren.dashboard;

    const workChildren: Record<string, MenuItem> = {};
    ['conversations', 'agents', 'workflows', 'missionControl', 'community'].forEach((k) => {
      if ((coreChildren as any)[k]) {
        workChildren[k] = (coreChildren as any)[k];
      }
    });

    const buildSectionChildren: Record<string, MenuItem> = {};
    ['flowBuilder', 'toolsLibrary'].forEach((k) => {
      if ((buildChildren as any)[k]) {
        buildSectionChildren[k] = (buildChildren as any)[k];
      }
    });

    const systemChildren: Record<string, MenuItem> = {};
    if ((platformChildren as any).integrations) {
      systemChildren.integrations = (platformChildren as any).integrations;
    }
    if ((buildChildren as any).aiConsole) {
      systemChildren.models = {
        ...(buildChildren as any).aiConsole,
        label: 'Models',
      };
    }
    if ((platformChildren as any).mcpServers) {
      systemChildren.mcpServers = (platformChildren as any).mcpServers;
    }

    const workspaceSectionChildren: Record<string, MenuItem> = {};
    if ((workspaceChildren as any).projectManagement) {
      workspaceSectionChildren.projectManagement = (workspaceChildren as any).projectManagement;
    }

    const developerToolsChildren = (buildChildren as any).developerTools?.children || {};

    const composed: Record<string, MenuItem> = {};

    if (Object.keys(homeChildren).length > 0) {
      composed.HOME = {
        visible: true,
        label: 'Home',
        icon: 'Home',
        children: homeChildren,
      };
    }

    if (Object.keys(workChildren).length > 0) {
      composed.WORK = {
        visible: true,
        label: 'Work',
        icon: 'Briefcase',
        children: workChildren,
      };
    }

    composed.DOMAINS = domainsSection;

    if (Object.keys(buildSectionChildren).length > 0) {
      composed.BUILD = {
        visible: true,
        label: 'Build',
        icon: 'Wrench',
        children: buildSectionChildren,
      };
    }

    if (Object.keys(systemChildren).length > 0) {
      composed.SYSTEM = {
        visible: true,
        label: 'System',
        icon: 'Settings',
        children: systemChildren,
      };
    }

    if (Object.keys(workspaceSectionChildren).length > 0) {
      composed.WORKSPACE = {
        visible: true,
        label: 'Workspace',
        icon: 'Package',
        children: workspaceSectionChildren,
      };
    }

    if (Object.keys(developerToolsChildren).length > 0) {
      composed.DEVELOPER_TOOLS = {
        visible: true,
        label: 'Developer Tools',
        icon: 'Code',
        children: developerToolsChildren,
      };
    }

    return composed;
  };

  // Check if section is collapsed
  const isSectionCollapsed = (sectionKey: string) => {
    return collapsedSections.includes(sectionKey);
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setConfig(menuConfigData as MenuConfig);
    setCollapsedSections([]);
    localStorage.removeItem('menuPreferences');
  };

  // Update menu item properties
  const updateMenuItem = (itemPath: string, updates: Partial<MenuItem>) => {
    // This would typically involve API calls to save to backend
    // For now, we'll just update local state
    console.log('Updating menu item:', itemPath, updates);
  };

  // Refresh Quick Access items manually
  const refreshQuickAccess = async () => {
    setIsLoadingQuickAccess(true);
    try {
      const quickAccessPages = await PageUsageService.getQuickAccessRecommendations(4);
      
      if (quickAccessPages.length > 0) {
        const dynamicQuickAccess: Record<string, MenuItem> = {};
        
        quickAccessPages.forEach((page, index) => {
          const key = `quickAccess_${index}`;
          // First try to find icon from menu config, then fallback to category mapping
          const icon = findIconFromMenuConfig(page.pagePath, page.pageTitle);
          
          dynamicQuickAccess[key] = {
            visible: true,
            label: page.pageTitle,
            path: page.pagePath,
            icon: icon
          };
        });
        
        setQuickAccessItems(dynamicQuickAccess);
      }
    } catch (error) {
      console.error('Error refreshing quick access items:', error);
    } finally {
      setIsLoadingQuickAccess(false);
    }
  };

  return {
    config,
    visibleMenuItems: getVisibleMenuItems(),
    collapsedSections,
    quickAccessItems,
    isLoadingQuickAccess,
    toggleItemVisibility,
    toggleSectionCollapse,
    isSectionCollapsed,
    savePreferences,
    resetToDefaults,
    updateMenuItem,
    refreshQuickAccess,
    userPreferences: config.userPreferences
  };
};

export default useMenuConfig;
